import { TokenDto } from "../types/api";

/**
 * Check if a token is expired
 * @param token - The token to check
 * @returns true if token is expired, false otherwise
 */
export const isTokenExpired = (token: TokenDto | null): boolean => {
  if (!token) {
    return true;
  }

  const now = Math.floor(Date.now() / 1000); // Current time in seconds
  const issuedAt = token.issued_at || 0; // Use 0 if issued_at is not available
  const expiresAt = issuedAt + token.expires_in;

  // If issued_at is 0, assume token is expired to force refresh
  if (issuedAt === 0) {
    return true;
  }

  return now >= expiresAt;
};

/**
 * Check if a token will expire within the next N minutes
 * @param token - The token to check
 * @param minutes - Number of minutes before expiration to consider "expiring soon"
 * @returns true if token will expire within the specified minutes
 */
export const isTokenExpiringSoon = (
  token: TokenDto | null,
  minutes: number = 5
): boolean => {
  if (!token) {
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  const issuedAt = token.issued_at || 0; // Use 0 if issued_at is not available
  const expiresAt = issuedAt + token.expires_in;
  const warningTime = minutes * 60; // Convert minutes to seconds

  // If issued_at is 0, assume token is expiring soon to force refresh
  if (issuedAt === 0) {
    return true;
  }

  return expiresAt - now <= warningTime;
};

/**
 * Get the time remaining until token expiration in seconds
 * @param token - The token to check
 * @returns Number of seconds until expiration, or 0 if expired
 */
export const getTokenTimeRemaining = (token: TokenDto | null): number => {
  if (!token) {
    return 0;
  }

  const now = Math.floor(Date.now() / 1000);
  const issuedAt = token.issued_at || 0; // Use 0 if issued_at is not available
  const expiresAt = issuedAt + token.expires_in;

  // If issued_at is 0, return 0 to force refresh
  if (issuedAt === 0) {
    return 0;
  }

  return Math.max(0, expiresAt - now);
};

/**
 * Calculate the time until token expiration in milliseconds
 * @param token - The token to check
 * @returns Number of milliseconds until expiration, or 0 if expired
 */
export const getTokenTimeRemainingMs = (token: TokenDto | null): number => {
  return getTokenTimeRemaining(token) * 1000;
};
